import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { intermediairesAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const PAYS_LIST = ['France','Belgique','Allemagne','Espagne','Italie','Pays-Bas','Suisse','Royaume-Uni'];
const EMPTY_ADDR = { adresse_1:'', adresse_2:'', code_postal:'', ville:'', pays:'France', est_principale:true };
const EMPTY_CTT  = { titre:'M.', nom:'', prenom:'', fonction:'', email:'', telephone:'', destinataire_comms:true };

const lbl = { fontSize:12, color:'#64748B', display:'block', marginBottom:6, fontWeight:500 };
const inp = { border:'1px solid #D9D9D5', borderRadius:8, padding:'10px 12px', width:'100%', fontSize:14, boxSizing:'border-box', fontFamily:'inherit', outline:'none' };

const ORANGE = '#F18A00';
const BLACK  = '#161616';
const SvgIcon = ({ size = 16, color = 'currentColor', children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);
const TAB_ICONS = [
  (c) => (<SvgIcon color={c}><path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2zM8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2M12 12l0 .01M3 13a20 20 0 0 0 18 0"/></SvgIcon>),
  (c) => (<SvgIcon color={c}><path d="M9 7a4 4 0 1 0 0 0M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0 -3 -3.85"/></SvgIcon>),
  (c) => (<SvgIcon color={c}><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/></SvgIcon>),
];

function Field({ label, value, onChange, placeholder, type='text', mt=0 }) {
  const [focus, setFocus] = React.useState(false);
  return (
    <div style={{ marginTop:mt }}>
      {label && <label style={lbl}>{label}</label>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
        placeholder={placeholder}
        style={{ ...inp, borderColor: focus ? ORANGE : '#D9D9D5',
          boxShadow: focus ? '0 0 0 3px rgba(241,138,0,0.15)' : 'none', transition:'all .12s' }} />
    </div>
  );
}

export default function FicheIntermediaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isReadOnly } = useAuth();
  const isEdit = Boolean(id);

  const [slide, setSlide]   = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Slide 1
  const [type, setType]                       = useState('courtier');
  const [nom, setNom]                         = useState('');
  const [statutJuridique, setStatutJuridique] = useState('');
  const [nImmatriculation, setNImmatriculation] = useState('');
  const [nProfessionnel, setNProfessionnel]   = useState('');
  const [codeReference, setCodeReference]     = useState('');
  const [adresses, setAdresses]               = useState([{ ...EMPTY_ADDR }]);

  // Slide 2
  const [contacts, setContacts] = useState([{ ...EMPTY_CTT }]);

  // Slide 3
  const [remunere, setRemunere]           = useState(false);
  const [tauxCommission, setTauxCommission] = useState('');

  const TYPES = [
    { value:'courtier',            label:t('intermediaires.type_courtier') },
    { value:'avocat',              label:t('intermediaires.type_avocat') },
    { value:'association',         label:t('intermediaires.type_association') },
    { value:'organisme_formation', label:t('intermediaires.type_formation') },
  ];
  const SLIDES = [t('intermediaires.tab_profile'), t('intermediaires.tab_contacts'), t('intermediaires.tab_contract')];

  useEffect(() => {
    if (!isEdit) return;
    intermediairesAPI.get(id).then(data => {
      setType(data.type || 'courtier');
      setNom(data.nom || '');
      setStatutJuridique(data.statut_juridique || '');
      setNImmatriculation(data.n_immatriculation || '');
      setNProfessionnel(data.n_professionnel || '');
      setCodeReference(data.code_reference || '');
      setRemunere(data.remunere || false);
      setTauxCommission(data.taux_commission || '');
      if (data.adresses?.length) setAdresses(data.adresses);
      if (data.contacts?.length) setContacts(data.contacts);
    }).catch(e => setError(e.message));
  }, [id, isEdit]);

  async function save() {
    if (!nom.trim()) { setError(t('intermediaires.structure_name') + ' — ' + t('common.required')); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        type, nom, statut_juridique:statutJuridique, n_immatriculation:nImmatriculation,
        n_professionnel:nProfessionnel, code_reference:codeReference,
        remunere, taux_commission:remunere ? parseFloat(tauxCommission) : null,
        adresses, contacts,
      };
      if (isEdit) await intermediairesAPI.update(id, payload);
      else        await intermediairesAPI.create(payload);
      navigate('/intermediaires');
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  function updateAddr(i, field, val) { setAdresses(p => p.map((a,ix) => ix===i ? {...a,[field]:val} : a)); }
  function addAddr()    { setAdresses(p => [...p, {...EMPTY_ADDR, est_principale:false}]); }
  function removeAddr(i){ setAdresses(p => p.filter((_,ix) => ix!==i)); }
  function updateCtt(i, field, val) { setContacts(p => p.map((c,ix) => ix===i ? {...c,[field]:val} : c)); }
  function addCtt()    { setContacts(p => [...p, {...EMPTY_CTT, destinataire_comms:false}]); }
  function removeCtt(i){ if (i===0) return; setContacts(p => p.filter((_,ix) => ix!==i)); }

  // ── Slide 1 ──────────────────────────────────────────────────────
  function renderS1() {
    return (
      <div>
        {/* Type */}
        <div style={{ marginBottom:16 }}>
          <label style={lbl}>{t('intermediaires.type_label')}</label>
          <div style={{ display:'flex', flexWrap:'wrap', gap:7, marginTop:5 }}>
            {TYPES.map(tp => (
              <button key={tp.value} onClick={() => setType(tp.value)}
                style={{ padding:'6px 14px', borderRadius:99, fontSize:14, cursor:'pointer', fontWeight:type===tp.value?700:400,
                  background:type===tp.value?'#1A1A1A':'#fff', color:type===tp.value?'#fff':'#64748B',
                  border:`1px solid ${type===tp.value?'#1A1A1A':'#CBD5E1'}` }}>
                {tp.label}
              </button>
            ))}
          </div>
        </div>

        {/* Courtier-specific fields */}
        {type === 'courtier' && (
          <div style={{ background:'#EFF6FF', borderRadius:7, padding:'12px 14px', marginBottom:14, border:'1px solid #BFDBFE' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#1D4ED8', marginBottom:10, textTransform:'uppercase', letterSpacing:'.06em' }}>
              {t('intermediaires.courtier_fields_title')}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label={t('intermediaires.professional_number')} value={nProfessionnel} onChange={setNProfessionnel} placeholder={t('intermediaires.professional_number_placeholder')} />
              <Field label={t('intermediaires.reference_code')}      value={codeReference}  onChange={setCodeReference}  placeholder={t('intermediaires.reference_code_placeholder')} />
            </div>
          </div>
        )}

        {/* Main fields */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
          <Field label={t('intermediaires.structure_name')} value={nom} onChange={setNom} placeholder="Ex. Cabinet Dupont & Associés" />
          <div>
            <label style={lbl}>{t('intermediaires.legal_status')}</label>
            <select value={statutJuridique} onChange={e => setStatutJuridique(e.target.value)} style={inp}>
              <option value="">—</option>
              {['SARL','EURL','SAS','SASU','SA','SNC','Association loi 1901','Auto-entrepreneur','Other'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <Field label={t('intermediaires.registration_number')} value={nImmatriculation} onChange={setNImmatriculation} placeholder={t('intermediaires.registration_placeholder')} />
        </div>

        {/* Addresses */}
        <div style={{ fontSize:11, fontWeight:700, color:'#64748B', textTransform:'uppercase', letterSpacing:'.06em', paddingBottom:8, borderBottom:'1px solid #F1F5F9', marginBottom:12 }}>
          {t('intermediaires.addresses')}
        </div>
        {adresses.map((addr, i) => (
          <div key={i} style={{ border:`1px solid ${addr.est_principale?'#BFDBFE':'#E2E8F0'}`, borderRadius:7, padding:12, marginBottom:10, background:addr.est_principale?'#F0F9FF':'#fff' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:12, fontWeight:600, color:addr.est_principale?'#0369A1':'#64748B' }}>
                {addr.est_principale ? t('intermediaires.main_address') : `${t('intermediaires.addresses')} ${i+1}`}
              </span>
              {!addr.est_principale && (
                <button onClick={() => removeAddr(i)} style={{ fontSize:12, color:'#DC2626', background:'none', border:'none', cursor:'pointer' }}>
                  {t('common.delete')}
                </button>
              )}
            </div>
            <Field label={t('intermediaires.address_line1')} value={addr.adresse_1} onChange={v=>updateAddr(i,'adresse_1',v)} placeholder={t('intermediaires.address1_placeholder')} />
            <Field label={t('intermediaires.address_line2')} value={addr.adresse_2} onChange={v=>updateAddr(i,'adresse_2',v)} placeholder={t('intermediaires.address2_placeholder')} mt={8} />
            <div style={{ display:'grid', gridTemplateColumns:'120px 1fr 180px', gap:8, marginTop:8 }}>
              <Field label={t('intermediaires.postal_code')} value={addr.code_postal} onChange={v=>updateAddr(i,'code_postal',v)} placeholder={t('intermediaires.postal_placeholder')} />
              <Field label={t('intermediaires.city')}        value={addr.ville}       onChange={v=>updateAddr(i,'ville',v)} placeholder="Paris" />
              <div>
                <label style={lbl}>{t('common.country')}</label>
                <select value={addr.pays} onChange={e=>updateAddr(i,'pays',e.target.value)} style={inp}>
                  {PAYS_LIST.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
        <button onClick={addAddr}
          style={{ fontSize:14, color:'#2563EB', background:'none', border:'1px dashed #93C5FD', borderRadius:7, padding:'9px', cursor:'pointer', width:'100%' }}>
          {t('intermediaires.add_address')}
        </button>
      </div>
    );
  }

  // ── Slide 2 ──────────────────────────────────────────────────────
  function renderS2() {
    return (
      <div>
        {contacts.map((c, i) => (
          <div key={i} style={{ border:'1px solid #E2E8F0', borderRadius:7, padding:12, marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
              <span style={{ fontSize:12, fontWeight:700, color:'#64748B' }}>
                {i===0 ? t('intermediaires.main_contact') : `${t('intermediaires.tab_contacts')} ${i+1}`}
              </span>
              {i>0 && <button onClick={()=>removeCtt(i)} style={{ fontSize:12, color:'#DC2626', background:'none', border:'none', cursor:'pointer' }}>{t('common.delete')}</button>}
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:10, alignItems:'flex-end' }}>
              <div>
                <label style={lbl}>{t('intermediaires.title_contact')}</label>
                <div style={{ display:'flex', gap:5 }}>
                  {['M.','Mme'].map(ti => (
                    <button key={ti} onClick={()=>updateCtt(i,'titre',ti)}
                      style={{ padding:'5px 11px', borderRadius:99, fontSize:13, cursor:'pointer',
                        background:c.titre===ti?'#1A1A1A':'#fff', color:c.titre===ti?'#fff':'#64748B',
                        border:`1px solid ${c.titre===ti?'#1A1A1A':'#CBD5E1'}`, fontWeight:c.titre===ti?700:400 }}>
                      {ti}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ flex:1 }}>
                <Field label={t('intermediaires.role')} value={c.fonction} onChange={v=>updateCtt(i,'fonction',v)} placeholder="Ex. Director" />
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <Field label={t('intermediaires.last_name')}  value={c.nom}       onChange={v=>updateCtt(i,'nom',v)}       placeholder="Dupont" />
              <Field label={t('intermediaires.first_name')} value={c.prenom}    onChange={v=>updateCtt(i,'prenom',v)}    placeholder="Jean" />
              <Field label={t('intermediaires.email')}      value={c.email}     onChange={v=>updateCtt(i,'email',v)}     placeholder="jean@cabinet.fr" type="email" />
              <Field label={t('intermediaires.phone')}      value={c.telephone} onChange={v=>updateCtt(i,'telephone',v)} placeholder="+33 6 12 34 56 78" type="tel" />
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:14, cursor:'pointer' }}>
              <input type="checkbox" checked={c.destinataire_comms} onChange={e=>updateCtt(i,'destinataire_comms',e.target.checked)} />
              {t('intermediaires.comm_recipient')}
            </label>
          </div>
        ))}
        <button onClick={addCtt}
          style={{ fontSize:14, color:'#2563EB', background:'none', border:'1px dashed #93C5FD', borderRadius:7, padding:'9px', cursor:'pointer', width:'100%' }}>
          {t('intermediaires.add_contact')}
        </button>
      </div>
    );
  }

  // ── Slide 3 ──────────────────────────────────────────────────────
  function renderS3() {
    return (
      <div>
        <div style={{ background:'#F8FAFC', borderRadius:8, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:600 }}>{t('intermediaires.remunerated')}</div>
            <div style={{ fontSize:12, color:'#64748B', marginTop:3 }}>{t('intermediaires.remunerated_desc')}</div>
          </div>
          <div style={{ display:'flex', gap:7 }}>
            {[false,true].map(v => (
              <button key={String(v)} onClick={() => setRemunere(v)}
                style={{ padding:'6px 16px', borderRadius:99, fontSize:14, cursor:'pointer', fontWeight:remunere===v?700:400,
                  background:remunere===v?'#1A1A1A':'#fff', color:remunere===v?'#fff':'#64748B',
                  border:`1px solid ${remunere===v?'#1A1A1A':'#CBD5E1'}` }}>
                {v ? t('common.yes') : t('common.no')}
              </button>
            ))}
          </div>
        </div>

        {remunere ? (
          <div style={{ maxWidth:280 }}>
            <label style={lbl}>{t('intermediaires.commission_rate')}</label>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
              <input type="number" value={tauxCommission} onChange={e=>setTauxCommission(e.target.value)}
                min="0" max="100" step="0.1" placeholder="0.00"
                style={{ ...inp, width:110 }} />
              <span style={{ fontSize:16, fontWeight:600, color:'#64748B' }}>%</span>
            </div>
            <div style={{ fontSize:12, color:'#64748B', marginTop:6 }}>{t('intermediaires.commission_auto')}</div>
          </div>
        ) : (
          <div style={{ background:'#F8FAFC', borderRadius:7, padding:'12px 14px', display:'flex', gap:10 }}>
            <span>ℹ️</span>
            <div style={{ fontSize:14, color:'#64748B' }}>{t('intermediaires.not_remunerated_info')}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ margin:'0 auto' }}>
      <div style={{ background:'#fff', borderRadius:12, border:'0.5px solid #E8E8E5', overflow:'hidden' }}>
        {/* Header noir */}
        <div style={{ background:BLACK, padding:'16px 20px', display:'flex', alignItems:'center', gap:13 }}>
          <button onClick={()=>navigate('/intermediaires')}
            style={{ background:'#222', border:'none', width:34, height:34, borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <SvgIcon size={18} color="#B8B8B8"><path d="M15 6l-6 6l6 6"/></SvgIcon>
          </button>
          <div style={{ width:42, height:42, borderRadius:10, background:ORANGE, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <SvgIcon size={22} color={BLACK}><path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2zM8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2M12 12l0 .01M3 13a20 20 0 0 0 18 0"/></SvgIcon>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, color:'#7A7A7A', textTransform:'uppercase', letterSpacing:'.04em' }}>{t('intermediaires.breadcrumb')}</div>
            <div style={{ fontSize:16, fontWeight:500, color:'#fff' }}>{isEdit ? (nom || t('intermediaires.edit_title')) : t('intermediaires.new_title')}</div>
          </div>
        </div>

        {/* Onglets avec icônes */}
        <div style={{ display:'flex', borderBottom:'0.5px solid #F1F1EE', padding:'0 14px', gap:2 }}>
          {SLIDES.map((s,i) => {
            const active = slide===i;
            const IconFn = TAB_ICONS[i] || TAB_ICONS[0];
            return (
              <button key={i} onClick={()=>setSlide(i)}
                style={{ padding:'11px 14px', fontSize:13, cursor:'pointer', background:'none', border:'none',
                  color:active?ORANGE:'#64748B', fontWeight:active?500:400,
                  borderBottom:active?`2px solid ${ORANGE}`:'2px solid transparent',
                  display:'flex', alignItems:'center', gap:7 }}>
                {IconFn(active?ORANGE:'#64748B')}
                {s}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div style={{ padding:20, minHeight:380 }}>
          {error && <div style={{ background:'#FEE2E2', color:'#991B1B', borderRadius:6, padding:'8px 12px', marginBottom:14, fontSize:14 }}>{error}</div>}
          {slide===0 && renderS1()}
          {slide===1 && renderS2()}
          {slide===2 && renderS3()}
        </div>

        {/* Footer */}
        <div style={{ padding:'11px 18px', borderTop:'1px solid #F1F5F9', background:'#F8FAFC', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <button onClick={()=>navigate('/intermediaires')} style={{ fontSize:14, color:'#64748B', background:'none', border:'none', cursor:'pointer' }}>
            {t('common.cancel')}
          </button>
          <span style={{ fontSize:12, color:'#94A3B8' }}>{t('common.slide')} {slide+1} / {SLIDES.length}</span>
          <div style={{ display:'flex', gap:8 }}>
            {slide>0 && <button onClick={()=>setSlide(s=>s-1)} style={{ fontSize:14, padding:'7px 16px', borderRadius:6, border:'1px solid #CBD5E1', cursor:'pointer', background:'#fff' }}>{t('common.previous')}</button>}
            {slide < SLIDES.length-1
              ? <button onClick={()=>setSlide(s=>s+1)} style={{ fontSize:14, padding:'7px 16px', borderRadius:6, border:'1px solid #CBD5E1', cursor:'pointer', background:'#fff' }}>{t('common.next')}</button>
              : (!isReadOnly && <button onClick={save} disabled={saving} style={{ fontSize:14, padding:'7px 16px', borderRadius:6, background:'#F18A00', color:'#161616', border:'none', cursor:'pointer', fontWeight:500 }}>
                  {saving ? t('common.saving') : '✓ '+t('common.save')}
                </button>)
            }
          </div>
        </div>
      </div>
    </div>
  );
}
